import * as THREE from "three";
import type { Vec3 } from "@genjiball/protocol";
import { BallTrail } from "../render/ballTrail.js";

/**
 * Ball renderer.
 *
 * The ball uses **forward prediction** rather than interpolation-with-delay:
 * each snapshot carries `pos + vel`, and at render time we project forward
 * from the latest snapshot by `(renderTime - snapshotTime) * vel`. This keeps
 * the rendered position aligned with the SERVER's authoritative ball, so a
 * shield press happens when the visible ball reaches the character rather
 * than 100ms before.
 *
 * Trade-off: predictions are linear, so the rendered ball may slightly
 * mispredict during sharp homing turns. Snapshots arriving at 15Hz keep the
 * mispredict bounded to ~67ms of integration error.
 */
export class Ball {
  readonly group: THREE.Group;
  private mesh: THREE.Mesh;
  readonly trail: BallTrail;

  private lastSnap: { t: number; pos: Vec3; vel: Vec3 } | null = null;
  private clientOffset: number | null = null;
  // Rendered position is lerped toward the predicted position each frame so
  // snapshot-to-snapshot corrections look smooth instead of popping.
  private renderedPos: Vec3 | null = null;
  private lastFrameTimeMs: number = 0;
  // Higher = ball snaps to corrections faster (more responsive but more
  // popping). Lower = smoother but more visible lag during turns.
  private static readonly CATCHUP_PER_SEC = 22;

  constructor() {
    this.group = new THREE.Group();
    const geom = new THREE.SphereGeometry(0.3, 16, 12);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xffe57a,
      emissive: 0x442200,
      roughness: 0.4,
    });
    this.mesh = new THREE.Mesh(geom, mat);
    this.mesh.castShadow = true;
    this.group.add(this.mesh);

    this.trail = new BallTrail();
    this.group.add(this.trail.object);
  }

  ingest(serverTimeMs: number, pos: Vec3, vel: Vec3) {
    // First snapshot — establish the clock offset so renderTimeMs (Date.now())
    // and serverTimeMs share a frame of reference.
    if (this.clientOffset === null) {
      this.clientOffset = Date.now() - serverTimeMs;
    }
    this.lastSnap = { t: serverTimeMs, pos: [...pos] as Vec3, vel: [...vel] as Vec3 };
  }

  resetTrail(at: Vec3) {
    this.trail.reset(at);
    this.renderedPos = [...at] as Vec3;
  }

  update(renderTimeMs: number) {
    if (!this.lastSnap) return;
    const offset = this.clientOffset ?? 0;
    const serverNow = renderTimeMs - offset;
    const dtSnap = Math.max(0, (serverNow - this.lastSnap.t) / 1000);

    // Predict where the ball "should" be right now from the latest snapshot.
    const predicted: Vec3 = [
      this.lastSnap.pos[0] + this.lastSnap.vel[0] * dtSnap,
      this.lastSnap.pos[1] + this.lastSnap.vel[1] * dtSnap,
      this.lastSnap.pos[2] + this.lastSnap.vel[2] * dtSnap,
    ];

    // Lerp the rendered position toward the prediction. This catches up to
    // snapshot corrections smoothly instead of popping when the next snapshot
    // arrives with a slightly different trajectory (homing turns, bounces).
    if (this.renderedPos === null || this.lastFrameTimeMs === 0) {
      this.renderedPos = predicted;
    } else {
      const frameDt = Math.min(0.1, (renderTimeMs - this.lastFrameTimeMs) / 1000);
      const k = Math.min(1, Ball.CATCHUP_PER_SEC * frameDt);
      this.renderedPos = [
        this.renderedPos[0] + (predicted[0] - this.renderedPos[0]) * k,
        this.renderedPos[1] + (predicted[1] - this.renderedPos[1]) * k,
        this.renderedPos[2] + (predicted[2] - this.renderedPos[2]) * k,
      ];
    }
    this.lastFrameTimeMs = renderTimeMs;

    this.group.position.set(...this.renderedPos);
    this.trail.push(this.renderedPos);
  }

  dispose() {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.trail.dispose();
  }
}
